import { lastValueFrom } from 'rxjs';
import analytics from './analytics';
import { UserSession } from '../types';
import { apiObservable } from './api';

export interface UTMData {
  utm_medium?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_type?: string;
  entry_page?: string;
  ttclid?: string;
  fbclid?: string;
}

export function parseUTMQuery(): UTMData {
  const search = new URLSearchParams(window.location.search);

  const fields: (keyof UTMData)[] = [
    'utm_content',
    'utm_source',
    'utm_campaign',
    'utm_type',
    'utm_medium',
    'entry_page',
    // tiktok
    'ttclid',
    // facebook
    'fbclid',
  ];

  const [utm_content, utm_source, utm_campaign, utm_type, utm_medium, entry_page, ttclid, fbclid] =
    fields.map((key) => search.get(key));

  return {
    utm_medium,
    utm_source,
    utm_campaign,
    utm_type,
    utm_content,
    ttclid,
    fbclid,
    entry_page,
  };
}

export function storeNewUserUTMQuery(): void {
  const data = parseUTMQuery();
  sessionStorage.setItem('newUserUTM', JSON.stringify(data));
}

export function retrieveNewUserUTMQuery(): UTMData | null {
  const rawData = sessionStorage.getItem('newUserUTM');
  if (!rawData) {
    return null;
  }
  return {
    // defaults to empty string for API request
    utm_source: '',
    utm_campaign: '',
    utm_medium: '',
    utm_type: '',
    utm_content: '',
    ...JSON.parse(rawData),
  };
}

export function clearNewUserUTMQuery(): void {
  sessionStorage.removeItem('newUserUTM');
}

export async function submitNewUserUTMParams(userData: UserSession): Promise<void> {
  // analytics tracking
  let utmData = retrieveNewUserUTMQuery();
  if (utmData) {
    analytics.identify(userData, {
      ...utmData,
    });
  } else {
    // default for on-device sign-ups
    utmData = {
      utm_source: 'direct',
      utm_medium: window.app.isElectron ? 'mac' : 'web',
    };
  }
  clearNewUserUTMQuery();

  const data = {
    source: utmData.utm_source ?? '',
    medium: utmData.utm_medium ?? '',
    campaign: utmData.utm_campaign ?? '',
    type: utmData.utm_type ?? '',
    content: utmData.utm_content ?? '',
    entry_page: utmData.entry_page ?? '',
  };
  if (utmData.ttclid || utmData.fbclid) {
    data['properties'] = {
      ttclid: utmData.ttclid,
      fbclid: utmData.fbclid,
    };
  }

  try {
    await lastValueFrom(
      apiObservable(
        'POST',
        '/utm/',
        {
          ...data,
        },
        { apiVersion: 'v2' },
      ),
    );
  } catch (e) {
    // 400 failure in the case of submitting data for a user that already has it
    console.error(e);
  }
}

export function getRewardfulAffiliateCode(): string | undefined {
  const search = new URLSearchParams(window.location.search);
  let via = search.get('rewardful');
  if (!!via) {
    sessionStorage.setItem('rewardfulAffiliateCode', via);
    return via;
  }
  via = sessionStorage.getItem('rewardfulAffiliateCode');
  return via;
}

export function clearRewardfulAffiliateCode(): void {
  sessionStorage.removeItem('rewardfulAffiliateCode');
}
